import * as React from 'react';
import useSessionStore from '../state/session';

type MaybeString = string | null | undefined;

export interface ReporterUserContext {
  id: string | null;
  email: string | null;
  name: string | null;
  roles?: string[];
}

interface ReporterSourceState {
  session: ReturnType<typeof useSessionStore.getState>['session'];
  profile: ReturnType<typeof useSessionStore.getState>['profile'];
  roles: ReturnType<typeof useSessionStore.getState>['roles'];
}

function normalize(value: MaybeString): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function firstNonEmpty(...values: MaybeString[]): string {
  for (const value of values) {
    const normalized = normalize(value);
    if (normalized.length > 0) {
      return normalized;
    }
  }
  return '';
}

function deriveReporterContext(state: ReporterSourceState): ReporterUserContext | null {
  const session = state.session ?? null;
  const profile = state.profile ?? null;
  const roles = Array.isArray(state.roles)
    ? state.roles
        .map(normalize)
        .filter((value): value is string => value.length > 0)
    : [];

  const profileFullName = normalize(profile?.full_name);
  const profileNameFallback = normalize(
    [normalize(profile?.first_name), normalize(profile?.last_name)]
      .filter(Boolean)
      .join(' ')
  );
  const metadata = ((session?.user?.user_metadata ?? {}) as Record<string, unknown>);
  const metadataFullName = normalize(metadata['full_name'] as MaybeString);
  const metadataName = firstNonEmpty(metadataFullName, metadata['name'] as MaybeString);

  const resolvedName = firstNonEmpty(
    profileFullName,
    profileNameFallback,
    metadataName,
    session?.user?.email as MaybeString
  );

  const profileEmail = normalize(profile?.email);
  const sessionEmail = normalize(session?.user?.email as MaybeString);
  const resolvedEmail = firstNonEmpty(profileEmail, sessionEmail);

  const resolvedId = normalize(session?.user?.id as MaybeString);

  if (!session && !profile && !resolvedEmail && !resolvedName) {
    return null;
  }

  return {
    id: resolvedId || null,
    email: resolvedEmail || null,
    name: resolvedName || null,
    roles: roles.length ? roles : undefined,
  };
}

let latestReporterContext: ReporterUserContext | null = deriveReporterContext({
  session: useSessionStore.getState().session,
  profile: useSessionStore.getState().profile,
  roles: useSessionStore.getState().roles,
});

export function useReporterContextBridge(): ReporterUserContext | null {
  const session = useSessionStore((s) => s.session);
  const profile = useSessionStore((s) => s.profile);
  const roles = useSessionStore((s) => s.roles);

  React.useEffect(() => {
    latestReporterContext = deriveReporterContext({ session, profile, roles });
  }, [session, profile, roles]);

  return latestReporterContext;
}

export function getReporterContextSnapshot(): ReporterUserContext | null {
  if (!latestReporterContext) return null;
  return {
    id: latestReporterContext.id,
    email: latestReporterContext.email,
    name: latestReporterContext.name,
    roles: latestReporterContext.roles ? [...latestReporterContext.roles] : undefined,
  };
}
